import React from 'react'
import { useAuth } from '../contexts/AuthContext'
import { BOOKMAKER_LOGOS } from '../config/bookmakers'
import ToolTour, { useToolTour, openTour, InfoTip, MarkerKey, ProfitSketch, RecommendedFilters } from '../components/ToolTour'
import PropEvCard from './PropEvCard'

const C = {
  border: '#1b2538',
  borderStrong: '#26354f',
  text: '#f1f5f9',
  muted: '#94a3b8',
  dim: '#64748b',
  green: '#34d399',
  blue: '#60a5fa',
  amber: '#fbbf24',
}

// One prop, carried through slides 1 and 4: every bookie's Over price, with
// one well clear of the pack
const PICK = { player: 'Nick Daicos', line: 'Over 29.5 Disposals' }
const PACK = [
  { name: 'TAB', odds: 1.80, row: 0 }, { name: 'Dabble', odds: 1.82, row: 2 },
  { name: 'Sportsbet', odds: 1.84, row: 1 }, { name: 'Betr', odds: 1.85, row: 0 },
  { name: 'Bet365', odds: 1.87, row: 2 }, { name: 'Ladbrokes', odds: 1.89, row: 1 },
  { name: 'Unibet', odds: 1.91, row: 0 },
]
const OUTLIER = { name: 'Pointsbet', odds: 2.25 }
const FAIR = 1.96   // our estimate, from the pack's overs plus an assumed margin
const DIFF = (OUTLIER.odds / FAIR - 1) * 100

const panel = {
  boxSizing: 'border-box', borderRadius: '14px',
  background: 'linear-gradient(180deg, #0f1a2e 0%, #0a1222 100%)', border: `1px solid ${C.borderStrong}`,
}
const label = { fontSize: '10px', fontWeight: 700, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.08em' }

// Blue info callout for slide 1, the same one Education uses
function ResearchNote() {
  return (
    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginTop: '12px', padding: '10px 12px', borderRadius: '10px', backgroundColor: 'rgba(96,165,250,0.07)', border: '1px solid rgba(96,165,250,0.25)' }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '2px' }}><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></svg>
      <span style={{ fontSize: '13px' }}>
        <strong style={{ color: '#93c5fd' }}>A research tool.</strong> Outliers are a starting point for your own research, not recommendations to bet.
      </span>
    </div>
  )
}

// Slide 1 visual: the bookies' prices on one axis. The pack bunches up, our
// fair estimate sits just past it, and one price sits well clear.
function PriceSpread({ isMobile }) {
  const lo = 1.74, hi = 2.34
  const x = (v) => `${((v - lo) / (hi - lo)) * 100}%`
  const logo = isMobile ? 20 : 24
  const rowGap = isMobile ? 18 : 22
  const trackTop = 3 * rowGap + logo / 2 + 6
  const ticks = [1.8, 1.9, 2.0, 2.1, 2.2, 2.3]

  return (
    <div style={{ width: '100%', maxWidth: isMobile ? 'none' : '460px' }}>
      <div style={{ ...label, textAlign: 'center', marginBottom: isMobile ? '6px' : '8px' }}>{PICK.player} · {PICK.line}</div>
      <div className="tt-rise" style={{ ...panel, padding: isMobile ? '12px 14px 10px' : '14px 22px 12px' }}>
        <div style={{ position: 'relative', height: trackTop + 34 }}>
          {/* fair estimate */}
          <div className="tt-fade" style={{ position: 'absolute', top: 0, bottom: 14, left: x(FAIR), borderLeft: '1.5px dashed rgba(96,165,250,0.7)', animationDelay: '0.5s' }}>
            <span style={{ position: 'absolute', top: -2, left: 6, fontSize: isMobile ? '10px' : '11px', fontWeight: 700, color: C.blue, whiteSpace: 'nowrap' }}>Est. fair {FAIR.toFixed(2)}</span>
          </div>
          {/* the pack */}
          {PACK.map((b, i) => (
            <img key={b.name} className="tt-pop" src={BOOKMAKER_LOGOS[b.name]} alt="" style={{
              position: 'absolute', left: x(b.odds), top: b.row * rowGap, marginLeft: -logo / 2, width: logo, height: logo,
              borderRadius: '50%', objectFit: 'cover', boxSizing: 'border-box', border: '1.5px solid rgba(148,163,184,0.45)',
              animationDelay: `${i * 0.05}s`,
            }} />
          ))}
          <span className="tt-fade" style={{ position: 'absolute', top: trackTop + 14, left: x(1.855), transform: 'translateX(-50%)', fontSize: isMobile ? '10px' : '11px', fontWeight: 700, color: C.muted, whiteSpace: 'nowrap', animationDelay: '0.4s' }}>The pack</span>
          {/* the outlier */}
          <div className="tt-pop" style={{ position: 'absolute', left: x(OUTLIER.odds), top: rowGap - 4, marginLeft: -(logo + 8) / 2, display: 'flex', flexDirection: 'column', alignItems: 'center', animationDelay: '0.75s' }}>
            <img src={BOOKMAKER_LOGOS[OUTLIER.name]} alt="" style={{
              width: logo + 8, height: logo + 8, borderRadius: '50%', objectFit: 'cover', boxSizing: 'border-box',
              border: `2px solid ${C.green}`, boxShadow: '0 0 14px rgba(52,211,153,0.55)', animation: 'ttGreenRing 1.8s ease-out 1.2s infinite',
            }} />
          </div>
          <span className="tt-fade" style={{ position: 'absolute', top: trackTop + 14, left: x(OUTLIER.odds), transform: 'translateX(-50%)', fontSize: isMobile ? '10.5px' : '11.5px', fontWeight: 800, color: C.green, whiteSpace: 'nowrap', animationDelay: '0.9s' }}>
            {OUTLIER.odds.toFixed(2)} · +{DIFF.toFixed(1)}%
          </span>
          {/* axis */}
          <div style={{ position: 'absolute', top: trackTop, left: 0, right: 0, height: '2px', borderRadius: '1px', backgroundColor: C.borderStrong }} />
          {ticks.map(t => (
            <span key={t} style={{ position: 'absolute', top: trackTop + 4, left: x(t), transform: 'translateX(-50%)', fontSize: '9.5px', fontWeight: 600, color: '#475569', fontVariantNumeric: 'tabular-nums' }}>{t.toFixed(1)}</span>
          ))}
        </div>
      </div>
    </div>
  )
}

// Slide 2 visual: how this page differs from the EV page
const PAGES = [
  { name: 'EV page', color: C.blue, rgb: '96,165,250', lines: ['Over and under odds', 'Exact EV', 'Bets to act on'] },
  { name: 'Prop Outliers', color: C.amber, rgb: '251,191,36', lines: ['Over odds only', 'An estimated gap', 'A shortlist to research'] },
]

function ShortlistCompare({ isMobile }) {
  return (
    <div style={{ width: '100%', maxWidth: isMobile ? 'none' : '460px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: isMobile ? '8px' : '12px' }}>
        {PAGES.map((pg, i) => (
          <div key={pg.name} className="tt-rise" style={{
            ...panel, padding: isMobile ? '10px' : '12px 14px', border: `1px solid rgba(${pg.rgb},0.35)`,
            background: `radial-gradient(120% 90% at 100% 0%, rgba(${pg.rgb},0.10) 0%, rgba(${pg.rgb},0) 60%), linear-gradient(180deg, #0f1a2e 0%, #0a1222 100%)`,
            animationDelay: `${i * 0.15}s`,
          }}>
            <div style={{ fontSize: isMobile ? '13.5px' : '14.5px', fontWeight: 800, color: pg.color }}>{pg.name}</div>
            <div style={{ display: 'grid', gap: isMobile ? '4px' : '6px', marginTop: isMobile ? '6px' : '8px' }}>
              {pg.lines.map(l => (
                <div key={l} style={{ fontSize: isMobile ? '11.5px' : '12.5px', fontWeight: 600, color: C.text }}>{l}</div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// Slide 3. Min bookmakers sits bottom right so its (i) opens inside the card.
function MinBooksInfo() {
  return (
    <InfoTip label="Why min bookmakers matters" title="Min bookmakers" align="end">
      Our fair odds estimate is built from every bookie pricing the prop. The more there are, the better it gets. With only a few, one odd price can throw it out.
    </InfoTip>
  )
}

const RECOMMENDED = [
  { label: 'Sports', value: 'All' },
  { label: 'Bookmakers', value: 'Your accounts' },
  { label: 'Diff %', value: '3% to 20%' },
  { label: 'Starts in', value: '0 to 24 hrs' },
  { label: 'Kelly fraction', value: '½ Kelly' },
  { label: 'Min bookmakers', value: '5 or more', info: <MinBooksInfo /> },
]

// Slide 4: the real card, carrying slide 1's outlier. $60 is what the page
// shows for it at the default ½ Kelly on a $1,000 bankroll, rounded to $5.
function CardGuide({ isMobile }) {
  // Commence times are naive UTC, the same as the API sends them
  const commence = new Date(Date.now() + 3 * 60 * 60 * 1000).toISOString().slice(0, 19)
  const opp = {
    sport: 'AFL', match: 'Collingwood vs Carlton', market_type: 'player_disposals',
    selection: `${PICK.player} Over 29.5`, commence_time: commence,
    ev_percentage: DIFF, bookmakers: [{ name: OUTLIER.name }],
  }
  return (
    // zoom (not transform) so the card's layout box shrinks too and the key
    // below stays in the stage
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ width: '100%', maxWidth: isMobile ? '256px' : '340px' }}>
        <div style={{ zoom: isMobile ? 0.76 : 0.86 }}>
          <PropEvCard
            opp={opp}
            className="tt-card"
            diffClass="bc-ev-badge bc-ev-high"
            oddsText={OUTLIER.odds.toFixed(2)}
            stakeText="$60"
            markers={{ market: 1, pick: 2, books: 3, odds: 4, diff: 5, stake: 6, track: 7 }}
          />
        </div>
      </div>
      <MarkerKey isMobile={isMobile} labels={['Market', 'Pick', 'Bookmaker', 'Odds', 'Diff', 'Stake', 'Track']} />
    </div>
  )
}

function propEvTourSlides(isMobile) {
  return [
    {
      title: 'How Prop Outliers works',
      body: (
        <>
          {`Many player props only come with Over odds, so the bookie's margin can't be removed exactly. Instead we estimate fair odds from all the Overs on offer, and flag any bookie whose price sits well above that estimate. The % is the size of that gap: here Pointsbet's ${OUTLIER.odds.toFixed(2)} sits ${DIFF.toFixed(1)}% above our fair estimate of ${FAIR.toFixed(2)}.`}
          <ResearchNote />
        </>
      ),
      visual: <PriceSpread isMobile={isMobile} />,
    },
    {
      title: 'A shortlist, not a bet list',
      body: 'A big gap can mean a mispriced line, but it can also mean the bookie knows something the market doesn\'t. Do your own research before you back it, and treat the % as a guide rather than an exact edge.',
      visual: <ShortlistCompare isMobile={isMobile} />,
    },
    {
      title: 'Set your filters',
      body: 'Here\'s our recommended setup. Gaps above 20% are more often a reason than a mistake, so they\'re worth skipping. Once it\'s set, tap Save Current and set it as your default so it loads every time.',
      visual: <RecommendedFilters rows={RECOMMENDED} isMobile={isMobile} />,
    },
    {
      title: 'Using Prop Outliers',
      body: (
        <ol style={{ margin: 0, paddingLeft: '18px', display: 'grid', gap: '4px' }}>
          <li>Find an outlier with a positive edge, then do your own research to decide if it's worth backing.</li>
          <li>Tap the bookie logo to open the game.</li>
          <li>Check their odds match the card, then stake the amount shown.</li>
          <li>Tap <strong style={{ color: C.text }}>+</strong> to track it in your Tracker.</li>
        </ol>
      ),
      visual: <CardGuide isMobile={isMobile} />,
    },
    {
      title: 'You\'re ready to go',
      body: 'New outliers appear as prices move through the day. The ones that check out are value bets like any other, so expect swings and judge your results over hundreds of bets, not a few.',
      visual: <ProfitSketch isMobile={isMobile} headline="Research, then back it" sub="Judge it over hundreds of bets" />,
    },
  ]
}

export const openPropEvTour = () => openTour('prop_ev')

export default function PropEvTour({ isVisible, isMobile }) {
  const { hasAccess } = useAuth()
  const tour = useToolTour('prop_ev', { isVisible })
  return (
    <ToolTour
      open={tour.open}
      onClose={tour.close}
      slides={propEvTourSlides(isMobile)}
      eyebrow="Prop Outliers"
      isMobile={isMobile}
      finalAction={hasAccess('prop_ev') ? { label: 'Get into it' } : { label: 'Unlock with Sports Premium', onClick: () => { window.location.hash = '#/pricing' } }}
      learnMore={{ label: 'Learn more', onClick: () => window.dispatchEvent(new CustomEvent('bc-navigate', { detail: 'education' })) }}
    />
  )
}
